import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import { useNavigate } from "react-router-dom";
import "./styles/AdminMessages.css";

const AdminServiceAreas = () => {
  const [serviceAreas, setServiceAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState("");
  const navigate = useNavigate();

  // ✅ Get CSRF token from cookie
  useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrfToken="))
      ?.split("=")[1];
    if (token) setCsrfToken(token);
  }, []);

  // ✅ Fetch service areas
  const fetchServiceAreas = async () => {
    try {
      const response = await axios.get("/api/public/service-areas", {
        withCredentials: true,
      });
      setServiceAreas(response.data.service_areas || []);
    } catch (error) {
      console.error("🔥 Error fetching service areas:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceAreas();
  }, []);

  // ✅ Toggle active/inactive
  const toggleActive = async (id, currentStatus) => {
    if (!csrfToken) return console.error("🚨 Missing CSRF token.");

    try {
      await axios.put(
        `/api/admin/service-areas/${id}`,
        { is_active: currentStatus ? 0 : 1 },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );

      setServiceAreas((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, is_active: currentStatus ? 0 : 1 } : a
        )
      );
    } catch (error) {
      console.error("🔥 Error toggling service area:", error);
    }
  };

  // ✅ Update area_rank (dropdown change)
  const handleRankChange = async (id, newRank) => {
    if (!csrfToken) return console.error("🚨 Missing CSRF token.");

    try {
      await axios.put(
        `/api/admin/service-areas/${id}`,
        { area_rank: parseInt(newRank) },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );

      // Optimistic UI update
      setServiceAreas((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, area_rank: parseInt(newRank) } : a
        )
      );
    } catch (error) {
      console.error("🔥 Error updating area rank:", error);
    }
  };

  // ✅ Delete service area
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service area?"))
      return;
    if (!csrfToken) return console.error("🚨 Missing CSRF token.");

    try {
      await axios.delete(`/api/admin/service-areas/${id}`, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });

      setServiceAreas((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error("🔥 Error deleting service area:", error);
    }
  };

  // ✅ Add new service area
  const handleAddServiceArea = async (e) => {
    e.preventDefault();
    if (!csrfToken) return console.error("🚨 Missing CSRF token.");

    const form = e.currentTarget;
    const city = form.city.value.trim();
    const state = form.state.value.trim().toUpperCase();

    if (!city || !state) return;

    try {
      const response = await axios.post(
        "/api/admin/service-areas",
        { city, state },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );

      if (response.data.success) {
        setServiceAreas((prev) => [
          ...prev,
          response.data.new_area || { city, state, is_active: 1, area_rank: 1 },
        ]);
        form.reset();
      }
    } catch (error) {
      if (error.response?.status === 409) {
        alert("This service area already exists.");
      } else {
        console.error("🔥 Error adding service area:", error);
      }
    }
  };

  const activeAreas = serviceAreas.filter((a) => !!a.is_active);
  const inactiveAreas = serviceAreas.filter((a) => !a.is_active);

  // ✅ Reusable table renderer
  const renderTable = (areasSubset, archived = false) => (
    <div className="table-wrapper">
      <table className={`messages-table${archived ? " archived" : ""}`}>
        <thead>
          <tr>
            <th>ID</th>
            <th>City</th>
            <th>State</th>
            <th>Slug</th>
            <th>Status</th>
            <th>Rank</th>
            <th className="table-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {areasSubset.map((area) => (
            <tr key={area.id}>
              <td>{area.id}</td>
              <td>{area.city}</td>
              <td>{area.state}</td>
              <td>{area.slug}</td>
              <td>
                {area.is_active ? (
                  <span className="status-label" style={{ color: "#155724" }}>
                    Active
                  </span>
                ) : (
                  <span className="status-label" style={{ color: "#721c24" }}>
                    Inactive
                  </span>
                )}
              </td>
              {/* ✅ New Dropdown for Area Rank */}
              <td>
                <select
                  className="role-select" // ✅ Reuses same styles
                  value={area.area_rank || 1}
                  onChange={(e) =>
                    handleRankChange(area.id, e.target.value)
                  }
                >
                  {[1, 2, 3, 4, 5].map((rank) => (
                    <option key={rank} value={rank}>
                      {rank}
                    </option>
                  ))}
                </select>
              </td>

              <td className="table-actions">
                <button
                  className="btn edit-btn"
                  onClick={() => navigate(`/admin/service-areas/${area.id}/page`)}
                  title="Manage Page Content"
                >
                  <i className="fa-solid fa-pen-to-square"></i> Manage Page
                </button>

                <button
                  className="btn archive-btn"
                  onClick={() => toggleActive(area.id, !!area.is_active)}
                  title={area.is_active ? "Deactivate" : "Activate"}
                >
                  {area.is_active ? (
                    <>
                      <i className="fa-solid fa-ban"></i> Deactivate
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i> Activate
                    </>
                  )}
                </button>

                <button
                  className="btn delete-btn"
                  onClick={() => handleDelete(area.id)}
                  title="Delete"
                >
                  <i className="fa-solid fa-xmark"></i> Delete
                </button>
              </td>
            </tr>
          ))}
          {areasSubset.length === 0 && (
            <tr>
              <td colSpan={7} style={{ color: "#777", textAlign: "center" }}>
                No items to display.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="admin-messages-container">
      <h2 className="messages-title">Service Areas</h2>

      {loading ? (
        <p className="messages-loading">Loading service areas...</p>
      ) : serviceAreas.length === 0 ? (
        <p className="messages-empty">No service areas found.</p>
      ) : (
        <>
          <div className="table-section">
            <h3 className="table-section-title">Active Service Areas</h3>
            {renderTable(activeAreas, false)}
          </div>

          <div className="table-section">
            <h3 className="table-section-title">Inactive Service Areas</h3>
            {renderTable(inactiveAreas, true)}
          </div>
        </>
      )}

      {/* ✅ Add new service area form */}
      <div className="table-section">
        <h3 className="table-section-title">Add New Service Area</h3>
        <form
          className="add-area-form"
          onSubmit={handleAddServiceArea}
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label>City</label>
            <input type="text" name="city" placeholder="e.g. Ocala" required />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label>State</label>
            <input
              type="text"
              name="state"
              placeholder="e.g. FL"
              required
              maxLength={2}
            />
          </div>
          <button type="submit" className="btn archive-btn">
            <i className="fa-solid fa-plus"></i> Add
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminServiceAreas;
